import React, { useState, useCallback, useEffect, useRef } from 'react';
import TerrainIcon      from '@mui/icons-material/Terrain';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LayersIcon       from '@mui/icons-material/Layers';
import CloseIcon        from '@mui/icons-material/Close';
import './MapControlPanel.css';

const MapControlPanel = ({ mapRef }) => {
  const [open, setOpen]               = useState(false);
  const [terrainOn, setTerrainOn]     = useState(false);
  const [buildingsOn, setBuildingsOn] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // ── Mesure RENFORCÉE de la hauteur réelle de la pile de contrôles
  // Mapbox natifs (zoom/compass, géolocalisation, reset), publiée comme
  // variable CSS --map-native-stack-height sur .dh-map-wrap.
  //
  // Trois renforts par rapport à la version précédente, qui pouvait
  // capter une valeur figée trop tôt (avant que tous les contrôles
  // Mapbox natifs soient réellement ajoutés au DOM, en particulier sur
  // mobile où le chargement est parfois plus lent) :
  // 1. getBoundingClientRect() au lieu de offsetHeight — précision
  //    sous-pixel, jamais d'arrondi qui accumule une petite erreur.
  // 2. MutationObserver EN PLUS du ResizeObserver — capte l'ajout
  //    tardif d'un contrôle (ex: GeolocateControl monté après le
  //    premier rendu), pas seulement le redimensionnement d'un
  //    contrôle déjà présent.
  // 3. Un listener sur `resize` de fenêtre, pour forcer un recalcul
  //    si jamais un changement de breakpoint modifie la taille des
  //    boutons sans que le navigateur déclenche ResizeObserver à temps.
  useEffect(() => {
    const mapWrap = rootRef.current?.closest('.dh-map-wrap');
    if (!mapWrap) return undefined;

    let resizeObserver;
    let mutationObserver;
    let rafId;
    let currentStackEl = null;

    const updateHeight = () => {
      if (!currentStackEl) return;
      const rect = currentStackEl.getBoundingClientRect();
      mapWrap.style.setProperty('--map-native-stack-height', `${rect.height}px`);
    };

    const attach = () => {
      const stackEl = mapWrap.querySelector('.mapboxgl-ctrl-bottom-right');
      if (!stackEl) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      currentStackEl = stackEl;
      updateHeight();

      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(stackEl);

      // Capte l'ajout/suppression tardive de contrôles à l'intérieur
      // de la pile (ex: GeolocateControl monté après coup).
      mutationObserver = new MutationObserver(updateHeight);
      mutationObserver.observe(stackEl, { childList: true, subtree: true });
    };

    attach();
    window.addEventListener('resize', updateHeight);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const handleToggleTerrain = useCallback(() => {
    const next = !terrainOn;
    setTerrainOn(next);
    mapRef?.current?.setTerrain?.(next, 1.5);
  }, [terrainOn, mapRef]);

  const handleToggleBuildings = useCallback(() => {
    const next = !buildingsOn;
    setBuildingsOn(next);
    mapRef?.current?.setBuildings3D?.(next);
  }, [buildingsOn, mapRef]);

  const activeCount = (terrainOn ? 1 : 0) + (buildingsOn ? 1 : 0);

  return (
    <div className={`mcp-root ${open ? 'mcp-root--open' : ''}`} ref={rootRef}>
      <div className="mcp-panel" role="menu">
        <div className="mcp-panel__header">
          <span className="mcp-panel__title">Vue 3D</span>
          <span className="mcp-panel__hint">Aperçu du terrain</span>
        </div>

        <button
          type="button"
          className={`mcp-row ${terrainOn ? 'mcp-row--active' : ''}`}
          onClick={handleToggleTerrain}
        >
          <span className="mcp-row__icon">
            <TerrainIcon style={{ fontSize: 15 }} />
          </span>
          <span className="mcp-row__text">
            <span className="mcp-row__label">Relief 3D</span>
            <span className="mcp-row__sub">Terrain exagéré</span>
          </span>
          <span className={`mcp-switch ${terrainOn ? 'mcp-switch--on' : ''}`}>
            <span className="mcp-switch__knob" />
          </span>
        </button>

        <button
          type="button"
          className={`mcp-row ${buildingsOn ? 'mcp-row--active' : ''}`}
          onClick={handleToggleBuildings}
        >
          <span className="mcp-row__icon">
            <LocationCityIcon style={{ fontSize: 15 }} />
          </span>
          <span className="mcp-row__text">
            <span className="mcp-row__label">Bâtiments 3D</span>
            <span className="mcp-row__sub">Extrusion des immeubles</span>
          </span>
          <span className={`mcp-switch ${buildingsOn ? 'mcp-switch--on' : ''}`}>
            <span className="mcp-switch__knob" />
          </span>
        </button>

        <div className="mcp-panel__footer">
          <span className={`mcp-footer-dot ${terrainOn ? 'mcp-footer-dot--on' : ''}`} />
          <span className={`mcp-footer-dot ${buildingsOn ? 'mcp-footer-dot--on' : ''}`} />
          <span className="mcp-footer-label">
            {activeCount === 0 ? 'Vue à plat' : activeCount === 1 ? '1 option active' : '2 options actives'}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={`mcp-fab ${activeCount > 0 ? 'mcp-fab--active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        title="Options de visualisation 3D"
      >
        <span className="mcp-fab__icon">
          {open ? <CloseIcon style={{ fontSize: 17 }} /> : <LayersIcon style={{ fontSize: 17 }} />}
        </span>
        {!open && activeCount > 0 && (
          <span className="mcp-fab__badge">{activeCount}</span>
        )}
      </button>
    </div>
  );
};

export default MapControlPanel;